import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Share2, MapPin, AlertTriangle, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { CommunityPost } from '../../data/mockData';

interface PostCardProps {
  post: CommunityPost;
  index: number;
}

const PostCard: React.FC<PostCardProps> = ({ post, index }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [showFullContent, setShowFullContent] = useState(false);

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const shouldTruncate = post.content.length > 280;
  const displayContent = shouldTruncate && !showFullContent 
    ? post.content.slice(0, 280) + '...' 
    : post.content;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`bg-dark-800/50 backdrop-blur-sm border rounded-xl p-6 hover:border-dark-600 transition-all duration-300 ${
        post.emergency ? 'border-accent-red/50 bg-accent-red/5' : 'border-dark-700'
      }`}
    >
      {/* Emergency Badge */}
      {post.emergency && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center space-x-2 mb-4 text-accent-red"
        >
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-semibold">EMERGENCY POST</span>
        </motion.div>
      )}

      {/* Author Info */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="relative">
          <img
            src={post.author.avatar}
            alt={post.author.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-dark-600"
          />
          {post.author.verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary-400 rounded-full flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-white">{post.author.name}</h3>
            {post.author.verified && (
              <CheckCircle className="w-4 h-4 text-primary-400" />
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <MapPin className="w-3 h-3" />
            <span>{post.location}</span>
            <span>•</span>
            <span>{formatDistanceToNow(post.timestamp, { addSuffix: true })}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
          {displayContent}
        </p>
        
        {shouldTruncate && (
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="text-primary-400 hover:text-primary-300 text-sm mt-2 transition-colors"
          >
            {showFullContent ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden">
            {post.images.map((image, imgIndex) => (
              <motion.img
                key={imgIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 + imgIndex * 0.1 }}
                src={image}
                alt=""
                className="w-full h-32 object-cover hover:scale-105 transition-transform cursor-pointer"
              />
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-primary-400/20 text-primary-300 rounded-full text-sm hover:bg-primary-400/30 transition-colors cursor-pointer"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-dark-700">
        <div className="flex items-center space-x-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleLike}
            className={`flex items-center space-x-2 transition-colors ${
              isLiked ? 'text-accent-red' : 'text-gray-400 hover:text-accent-red'
            }`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-sm">{likeCount}</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex items-center space-x-2 text-gray-400 hover:text-primary-400 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm">{post.comments}</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex items-center space-x-2 text-gray-400 hover:text-primary-400 transition-colors"
          >
            <Share2 className="w-5 h-5" />
          </motion.button>
        </div>

        {post.emergency && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-accent-red to-accent-orange px-4 py-2 rounded-lg text-white font-semibold text-sm hover:shadow-lg transition-all duration-200"
          >
            Help Now
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default PostCard;